import React, { useState } from 'react';
import Icon, { IconName } from './Icon';
import { useT } from '../i18n';

export interface FormFieldProps {
  id: string;
  label: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'textarea' | 'select';
  value?: string | number;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  hint?: string;
  icon?: IconName;
  options?: { value: string | number; label: string; disabled?: boolean }[];
  /** Grouped select options; when set they replace `options`. */
  optionGroups?: { label: string; options: { value: string | number; label: string }[] }[];
  rows?: number;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'bordered';
  onChange?: (value: string | number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  style?: React.CSSProperties;
  name?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  step?: string;
  preserveNumberAsString?: boolean;
}

const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  type = 'text',
  value = '',
  placeholder,
  required = false,
  disabled = false,
  error,
  hint,
  icon,
  options = [],
  optionGroups,
  rows = 3,
  size = 'md',
  variant = 'default',
  onChange,
  onBlur,
  onFocus,
  className,
  style,
  name,
  inputMode,
  step,
  preserveNumberAsString = false
}) => {
  const t = useT();
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Size configurations
  const sizeConfig = {
    sm: {
      height: '32px',
      padding: '6px 12px',
      fontSize: '13px',
      labelSize: '12px',
      iconSize: '14px'
    },
    md: {
      height: '40px',
      padding: '8px 16px',
      fontSize: '14px',
      labelSize: '14px',
      iconSize: '16px'
    },
    lg: {
      height: '48px',
      padding: '12px 20px',
      fontSize: '16px',
      labelSize: '16px',
      iconSize: '18px'
    }
  };

  // Variant configurations
  const variantConfig = {
    default: {
      background: '#ffffff',
      border: '#d1d5db',
      focusBorder: '#08B8D3',
      errorBorder: '#ef4444'
    },
    filled: {
      background: '#f9fafb',
      border: 'transparent',
      focusBorder: '#08B8D3',
      errorBorder: '#ef4444'
    },
    bordered: {
      background: '#ffffff',
      border: '#374151',
      focusBorder: '#08B8D3',
      errorBorder: '#ef4444'
    }
  };

  const currentSize = sizeConfig[size];
  const currentVariant = variantConfig[variant];

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    fontFamily: 'Inter, system-ui, sans-serif',
    ...style
  };

  const labelStyle: React.CSSProperties = {
    fontSize: currentSize.labelSize,
    fontWeight: '500',
    color: error ? '#ef4444' : '#374151',
    marginBottom: '4px',
    lineHeight: '1.4'
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const getInputStyle = (): React.CSSProperties => {
    let borderColor = currentVariant.border;
    if (error) {
      borderColor = currentVariant.errorBorder;
    } else if (isFocused) {
      borderColor = currentVariant.focusBorder;
    }

    const horizontalPadding = currentSize.padding.split(' ')[1];
    const iconPadding = icon ? `${Number.parseInt(horizontalPadding, 10) + 24}px` : horizontalPadding;

    return {
      width: '100%',
      height: type === 'textarea' ? 'auto' : currentSize.height,
      padding: currentSize.padding,
      paddingLeft: iconPadding,
      paddingRight: type === 'password' ? '40px' : horizontalPadding,
      fontSize: currentSize.fontSize,
      fontFamily: 'Inter, system-ui, sans-serif',
      background: currentVariant.background,
      border: `1px solid ${borderColor}`,
      borderRadius: '8px',
      outline: 'none',
      transition: 'all 0.2s ease-in-out',
      color: disabled ? '#9ca3af' : '#111827',
      cursor: disabled ? 'not-allowed' : 'text',
      resize: type === 'textarea' ? 'vertical' : 'none',
      ...(type === 'textarea' && { minHeight: `${rows * 1.5}em` })
    };
  };

  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: currentSize.iconSize,
    height: currentSize.iconSize,
    color: error ? '#ef4444' : '#6b7280',
    pointerEvents: 'none',
    zIndex: 1
  };

  if (!error && isFocused) {
    iconStyle.color = '#08B8D3';
  }

  const passwordToggleStyle: React.CSSProperties = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '16px',
    height: '16px',
    color: '#6b7280',
    cursor: 'pointer',
    zIndex: 1
  };

  const messageStyle: React.CSSProperties = {
    fontSize: '12px',
    lineHeight: '1.4',
    color: error ? '#ef4444' : '#6b7280',
    marginTop: '4px'
  };

  const coerceChangeValue = (rawValue: string): string | number => {
    if (type !== 'number') return rawValue;
    if (preserveNumberAsString) return rawValue;
    return Number.parseFloat(rawValue) || '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const newValue = coerceChangeValue(e.target.value);
    onChange?.(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const renderInput = () => {
    const commonProps = {
      id: id,
      name: name || id,
      value: value,
      placeholder: placeholder,
      required: required,
      disabled: disabled,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onChange: handleInputChange,
      style: getInputStyle()
    };

    switch (type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={rows}
          />
        );
      
      case 'select':
        return (
          <select {...commonProps}>
            <option value="" disabled>
              {placeholder || t('common.selectPlaceholder', { label: label.toLowerCase() })}
            </option>
            {optionGroups
              ? optionGroups.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.options.map((option) => (
                      <option key={String(option.value)} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))
              : options.map((option) => (
                  <option key={String(option.value)} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))}
          </select>
        );
      
      case 'password':
        return (
          <input
            {...commonProps}
            type={showPassword ? 'text' : 'password'}
          />
        );
      
      default:
        return (
          <input
            {...commonProps}
            type={type}
            inputMode={type === 'number' ? inputMode : undefined}
            step={type === 'number' ? step : undefined}
          />
        );
    }
  };

  return (
    <div className={className} style={containerStyle}>
      {/* Label */}
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
      </label>

      {/* Input Wrapper */}
      <div style={inputWrapperStyle}>
        {/* Icon */}
        {icon && (
          <Icon
            name={icon}
            style={iconStyle}
          />
        )}

        {/* Input Field */}
        {renderInput()}

        {/* Password Toggle */}
        {type === 'password' && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            style={{
              ...passwordToggleStyle,
              background: 'none',
              border: 'none',
              padding: 0
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        )}
      </div>

      {/* Error or Hint Message */}
      {(error || hint) && (
        <div style={messageStyle}>
          {error || hint}
        </div>
      )}
    </div>
  );
};

export default FormField;

export const coerceFormFieldValue = (args: {
  type?: FormFieldProps['type']
  rawValue: string
  preserveNumberAsString?: boolean
}): string | number => {
  if (args.type !== 'number') return args.rawValue
  if (args.preserveNumberAsString) return args.rawValue
  return Number.parseFloat(args.rawValue) || ''
}
